import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import { ShapeSelect, AlertNotice, InfoTooltip } from 'shared/components';
import {
  SPACING_FT_BY_SEATING_KEY,
  SHAPE_ICON_KEYS_BY_SEATING_KEY,
  transformOptions,
  resolveSeating,
} from 'shared/seatingArrangement';
import './RoomStructure.scss';

const MAX_SEATS_PER_ROW_OR_COLUMN = 25;
const MAX_TOTAL_SEATS = 200;

class RoomStructure extends Component {
  constructor(props) {
    super(props);

    const options = {
      seating: transformOptions(props.roomOptions.seating),
    };

    this.state = {
      seating: resolveSeating(options.seating, props.room.seating) || this.getDefaultSeating(options) || {},
      seatingRowCount: props.room.seatingRowCount || 0,
      seatingColumnCount: props.room.seatingColumnCount || 0,
      errors: {},
      options,
    };

    this.onSubmit = this.onSubmit.bind(this);
    this.onSelectSeating = this.onSelectSeating.bind(this);
    this.onSeatCountChange = this.onSeatCountChange.bind(this);
    this.isDisabled = this.isDisabled.bind(this);
  }

  // Hoisted out of the constructor (and reused by componentDidUpdate) so both call sites
  // apply the exact same "Rows & Columns" fallback rather than duplicating logic that can
  // drift out of sync.
  getDefaultSeating(options = this.state.options) {
    return options.seating.find((s) => s.key === 'rowsColumns');
  }

  // STATEDIST-11174 (review B1 fix): the parent (EventRoomsContainer) hands back a NEW
  // `room` object identity after every successful Structure & Seating autosave, since the
  // autosave response is merged into state via a fresh object. That response reflects
  // whatever was sent 800ms+ earlier — resetting local editing state on ANY identity
  // change silently reverted in-progress typing back to the stale autosaved value (proven
  // data loss: type "1" -> "12", let a stale autosave response land, "12" reverts to "1").
  //
  // Local state must only be re-seeded from props when we're now looking at a genuinely
  // DIFFERENT room (e.g. the parent has switched from editing one room to editing another),
  // not merely because the same room's object reference changed after an autosave echoed
  // its own fields back. `room.id` is the right identity signal for "different room": it
  // only changes here when the parent assigns/switches to a different persisted id
  // (including the one-time undefined -> real-id transition on this room's very first
  // create), never on the routine no-id -> no-id or same-id -> same-id round trips that
  // every subsequent autosave produces.
  componentDidUpdate(prevProps) {
    if (prevProps.room.id !== this.props.room.id) {
      this.setState({
        seating:
          resolveSeating(this.state.options.seating, this.props.room.seating) || this.getDefaultSeating() || {},
        seatingRowCount: this.props.room.seatingRowCount || 0,
        seatingColumnCount: this.props.room.seatingColumnCount || 0,
      });
    }
  }

  onSubmit(e) {
    e.preventDefault();

    // Note: distanceBetweenShoulders/distanceBetweenHeads are intentionally omitted here.
    // The spacingFt badge shown on this screen is display-only and is never persisted;
    // the backend (STATEDIST-11171) preserves any existing stored spacing when these
    // fields are absent from the payload.
    this.props.onContinue({
      shape: this.props.room.shape,
      seating: omit(this.state.seating, ['key']),
      seatingRowCount: this.state.seatingRowCount,
      seatingColumnCount: this.state.seatingColumnCount,
    });
  }

  // STATEDIST-11174: notifies the parent (EventRoomsContainer) of in-progress field state
  // so it can debounce an autosave, independent of onSubmit/onContinue. Fires with the same
  // field shape onSubmit sends (minus the 'key' bookkeeping field), but is a no-op when the
  // parent doesn't supply the optional onFieldChange prop (e.g. existing tests/consumers).
  notifyFieldChange(overrides = {}) {
    if (!this.props.onFieldChange) {
      return;
    }
    const nextState = { ...this.state, ...overrides };
    this.props.onFieldChange({
      shape: this.props.room.shape,
      seating: omit(nextState.seating, ['key']),
      seatingRowCount: nextState.seatingRowCount,
      seatingColumnCount: nextState.seatingColumnCount,
    });
  }

  onSelectSeating(selected) {
    this.setState({ seating: selected });
    this.notifyFieldChange({ seating: selected });
  }

  onSeatCountChange(field, rawValue) {
    let value = parseInt(rawValue, 10);
    if (Number.isNaN(value) || value < 0) {
      value = 0;
    }

    const errors = { ...this.state.errors };

    // An explicitly-entered 0 is invalid and must block Continue (matches legacy
    // SeatingDetails.jsx's errors.zeroNotAllowed behavior). This is distinct from the
    // field simply being empty/untouched, which is rendered as blank via `value || ''`.
    if (rawValue !== '' && value === 0) {
      errors[field] = 'errors.zeroNotAllowed';
    } else {
      // Clamp down to the max when the raw input exceeds it. Either way (clamped
      // or already in range) the resulting value is valid, so any stale error for
      // this field must be cleared rather than set.
      if (value > MAX_SEATS_PER_ROW_OR_COLUMN) {
        value = MAX_SEATS_PER_ROW_OR_COLUMN;
      }
      delete errors[field];
    }

    const rowCount = field === 'seatingRowCount' ? value : this.state.seatingRowCount;
    const colCount = field === 'seatingColumnCount' ? value : this.state.seatingColumnCount;
    if (rowCount * colCount > MAX_TOTAL_SEATS) {
      errors.seatCount = 'seatingChart.seatingDetails.nrOfSeatsError';
    } else {
      delete errors.seatCount;
    }

    this.setState({ [field]: value, errors });
    this.notifyFieldChange({ [field]: value });
  }

  isDisabled() {
    const { state } = this;
    // STATEDIST-11673: the "Optional Before Test Day · Required by End of Test Day" pill
    // (room-structure-header, below) promises seat count isn't mandatory yet — before test
    // day, a still-blank seat count no longer blocks Continue. Any real validation error
    // (an explicit 0, over the 25/200 caps) still blocks regardless of test day; this only
    // relaxes the "must be entered at all" requirement. isTestDayOrLater defaults to true
    // (fails safe / matches today's always-required behavior) if the caller doesn't know.
    const seatCountEntered = state.seatingRowCount > 0 && state.seatingColumnCount > 0;
    const seatCountValid =
      isEmpty(state.errors) && (!this.props.isTestDayOrLater || seatCountEntered);
    // An unmapped seating key (not reachable today, since /seatings only ever returns rows
    // this table knows about, but possible if a future /seatings row lacks a spacing entry)
    // must not be treated as a valid selection — it would otherwise let onSubmit silently
    // persist a 0 ft spacing for a compliance-facing value. Treat it the same as no seating selected.
    const seatingSpacingKnown = SPACING_FT_BY_SEATING_KEY[state.seating.key] !== undefined;
    return isEmpty(state.seating) || !seatingSpacingKnown || !seatCountValid;
  }

  // STATEDIST-11640: /seatings (STATEDIST-11171) already returns exactly the 2 rows this
  // screen shows when the flag is on, so the options are rendered directly — no
  // client-side grouping/matching against a fixed 4-option legacy list.
  renderSeatingArrangementTiles() {
    const { t } = this.props;
    const { seating } = this.state.options;

    return seating.map((option) => {
      const spacingFt = SPACING_FT_BY_SEATING_KEY[option.key];
      return (
        <ShapeSelect
          key={option.id}
          name="seatingArrangement"
          value={option.key}
          selected={this.state.seating.key === option.key}
          // A row /seatings returns that isn't in SPACING_FT_BY_SEATING_KEY has no known
          // spacing yet — omit the subtitle rather than interpolating an undefined count
          // (isDisabled() also treats this option as unselectable, see above).
          subtitle={spacingFt !== undefined ? t('roomBuilder.spacingFeetLabel', { count: spacingFt }) : undefined}
          shape={SHAPE_ICON_KEYS_BY_SEATING_KEY[option.key] || []}
          label={option.name}
          onChange={() => this.onSelectSeating(option)}
        />
      );
    });
  }

  render() {
    const { errorRoom, t } = this.props;
    const { seatingRowCount, seatingColumnCount, errors } = this.state;
    const totalSeats = seatingRowCount * seatingColumnCount;
    const seatCountEntered = seatingRowCount > 0 && seatingColumnCount > 0 && isEmpty(errors);
    // The 200-seat cap (errors.seatCount) is a property of the row/column PAIR, not either
    // field alone — 5x41 and 41x5 are equally "wrong" the same way, so it can't be
    // attributed to one input. It's surfaced as a shared, group-level message (below the
    // box) and referenced via aria-describedby on BOTH inputs, alongside each input's own
    // field-specific zero-not-allowed error.
    const rowDescribedBy =
      [errors.seatingRowCount && 'seatingRowCountError', errors.seatCount && 'seatCountGroupError']
        .filter(Boolean)
        .join(' ') || undefined;
    const columnDescribedBy =
      [errors.seatingColumnCount && 'seatingColumnCountError', errors.seatCount && 'seatCountGroupError']
        .filter(Boolean)
        .join(' ') || undefined;
    // Whichever field's error line is absent still reserves the same slot below its input —
    // otherwise a lone error under one field grows only that field's column height, and
    // center-aligning `.seat-count-box` shifts that field's input out of line with its
    // (shorter) sibling. Rendered in lockstep for both fields, not per-field, so an idle
    // (error-free) screen stays exactly as compact as before this existed.
    const anySeatCountFieldError = !!(errors.seatingRowCount || errors.seatingColumnCount);

    return (
      <form className="room-structure" onSubmit={this.onSubmit} noValidate>
        <div className="room-structure-header">
          <h4>{t('roomBuilder.roomStructureTitle')}</h4>
          <span className="status-pill">{t('roomBuilder.optionalNowPill')}</span>
        </div>

        <h5>
          <span id="seatingArrangementLabel">{t('roomBuilder.seatingArrangementLabel')}</span>
          <InfoTooltip text={t('roomBuilder.spacingTooltipText')} label={t('roomBuilder.spacingTooltipLabel')} />
        </h5>
        <div className="shapes-container" role="radiogroup" aria-labelledby="seatingArrangementLabel">
          {this.renderSeatingArrangementTiles()}
        </div>
        <div className="work-surface-notice">
          <AlertNotice message={t('roomBuilder.workSurfaceNotice')} type="information" />
        </div>

        <hr className="section-divider" />

        <div className="seat-count-section">
          <h5>
            <span>
              {t('roomBuilder.seatCountLabel')}
              <InfoTooltip text={t('roomBuilder.seatCountTooltipText')} label={t('roomBuilder.seatCountTooltipLabel')} />
            </span>
            {seatCountEntered && (
              <output className="entered-chip" htmlFor="seatingRowCount seatingColumnCount">
                {t('roomBuilder.enteredLabel')}
              </output>
            )}
          </h5>
          <div className="seat-count-box">
            <div className="seat-count-field">
              <label htmlFor="seatingRowCount">{t('roomBuilder.rowsLabel')}</label>
              <input
                id="seatingRowCount"
                type="number"
                min="0"
                value={seatingRowCount || ''}
                onChange={(e) => this.onSeatCountChange('seatingRowCount', e.target.value)}
                aria-invalid={!!(errors.seatingRowCount || errors.seatCount)}
                aria-describedby={rowDescribedBy}
              />
              {anySeatCountFieldError && (
                <span id="seatingRowCountError" className="text-error font-size-12 seat-count-field-error">
                  {errors.seatingRowCount ? t(errors.seatingRowCount) : ''}
                </span>
              )}
            </div>
            <span className="operator">&times;</span>
            <div className="seat-count-field">
              <label htmlFor="seatingColumnCount">{t('roomBuilder.columnsLabel')}</label>
              <input
                id="seatingColumnCount"
                type="number"
                min="0"
                value={seatingColumnCount || ''}
                onChange={(e) => this.onSeatCountChange('seatingColumnCount', e.target.value)}
                aria-invalid={!!(errors.seatingColumnCount || errors.seatCount)}
                aria-describedby={columnDescribedBy}
              />
              {anySeatCountFieldError && (
                <span id="seatingColumnCountError" className="text-error font-size-12 seat-count-field-error">
                  {errors.seatingColumnCount ? t(errors.seatingColumnCount) : ''}
                </span>
              )}
            </div>
            <span className="operator">=</span>
            <div className="total-seats-box">
              <strong>{totalSeats}</strong>
              <span>{t('roomBuilder.totalSeatsLabel')}</span>
            </div>
          </div>
          {errors.seatCount && (
            <div id="seatCountGroupError">
              <AlertNotice message={t(errors.seatCount)} type="error" />
            </div>
          )}
        </div>

        {!isEmpty(errorRoom) && (
          <div className="mt-3">
            <AlertNotice message={errorRoom.message} type="error" />
          </div>
        )}

        <div className="buttons-container w-100">
          <button type="button" className="btn btn-outline" onClick={this.props.onBack}>
            {t('common.labels.back')}
          </button>
          <button type="submit" className="btn btn-primary" disabled={this.isDisabled()}>
            {t('common.labels.continue')}
          </button>
        </div>
      </form>
    );
  }
}

RoomStructure.propTypes = {
  room: PropTypes.shape({
    shape: PropTypes.shape({}),
    seating: PropTypes.shape({}),
    seatingRowCount: PropTypes.number,
    seatingColumnCount: PropTypes.number,
  }).isRequired,
  roomOptions: PropTypes.shape({
    seating: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  errorRoom: PropTypes.shape({ message: PropTypes.string }),
  onBack: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
  // STATEDIST-11174: optional — notified with in-progress field state on every seating/seat
  // count change, so the parent can debounce an autosave. Omitting it is a no-op.
  onFieldChange: PropTypes.func,
  // STATEDIST-11673: whether the room's test day has arrived (or passed) — computed by
  // EventRoomsContainer from tcEvent/serverDateTime, not derived here. Seat count is only
  // required for Continue once this is true; see isDisabled().
  isTestDayOrLater: PropTypes.bool,
  t: PropTypes.func.isRequired,
};

RoomStructure.defaultProps = {
  errorRoom: {},
  onFieldChange: null,
  // Fails safe to today's always-required behavior if the caller doesn't know yet.
  isTestDayOrLater: true,
};

export default withTranslation()(RoomStructure);
